import { useCallback } from 'react';
import { getRandomGameLocation } from '../game/data/gameLocations';
import { getGameZoneForGpsLocation, resolveGpsLocation } from '../game/data/locationGps';
import { fetchLocationGpsConfig, getCachedLocationGpsConfig } from '../game/infrastructure/locationGpsApi';

export default function useLocatePlayer({ setZone, setGps, gpsRequestRef, gpsCoordsRef }) {
  const locate = useCallback(() => new Promise((done) => {
    const assign = (next, locationId = 'centro') => {
      // En torneo manda la locación del admin: un GPS que resuelve tarde no
      // debe pisar la zona ni la ambientación de la partida común.
      try {
        if (sessionStorage.getItem('nacanaca-active-tournament')) return;
      } catch {
        // Sin almacenamiento: asigna normal.
      }
      sessionStorage.setItem("nacanaca-zone", next);
      sessionStorage.setItem('nacanaca-scene-location', locationId);
      setZone(next);
    };
    const assignRandom = () => {
      const selected = getRandomGameLocation();
      assign(selected.gameZone, selected.id);
    };
    const persistGps = (next) => {
      setGps(next);
      try {
        if (next) sessionStorage.setItem("nacanaca-gps", JSON.stringify(next));
        else sessionStorage.removeItem("nacanaca-gps");
      } catch {
        // Sin almacenamiento: el pin solo vive en memoria.
      }
    };
    if (!navigator.geolocation) {
      assignRandom();
      persistGps({ status: "error", message: "Este dispositivo no tiene GPS." });
      done(null);
      return;
    }
    const requestId = (gpsRequestRef.current += 1);
    // No conserves coordenadas de un intento anterior mientras el navegador
    // resuelve un permiso nuevo. Jugar solo durante este estado debe salir
    // explícitamente sin GPS.
    gpsCoordsRef.current = null;
    persistGps({ status: "locating" });
    const resolveWithCoords = async (coords) => {
      let config;
      try {
        ({ config } = await fetchLocationGpsConfig());
      } catch {
        config = getCachedLocationGpsConfig();
      }
      if (gpsRequestRef.current !== requestId) return;
      const resolved = resolveGpsLocation(coords.latitude, coords.longitude, config);
      const accuracyM = Number.isFinite(coords.accuracy)
        ? Math.round(coords.accuracy)
        : null;
      if (resolved?.inside) {
        gpsCoordsRef.current = { lat: coords.latitude, lon: coords.longitude, accuracy: coords.accuracy };
        assign(getGameZoneForGpsLocation(resolved.locationId), resolved.locationId);
        persistGps({
          status: "inside",
          locationId: resolved.locationId,
          label: resolved.label,
          distanceKm: Math.round(resolved.distanceKm * 10) / 10,
          radiusKm: resolved.radiusKm,
          accuracyM,
          at: Date.now(),
        });
      } else if (resolved?.nearest) {
        gpsCoordsRef.current = { lat: coords.latitude, lon: coords.longitude, accuracy: coords.accuracy };
        assign(getGameZoneForGpsLocation(resolved.nearest.locationId), resolved.nearest.locationId);
        persistGps({
          status: "outside",
          locationId: null,
          label: null,
          distanceKm: null,
          radiusKm: null,
          accuracyM,
          nearestLabel: resolved.nearest.label,
          nearestDistanceKm: Math.round(resolved.nearest.distanceKm * 10) / 10,
          at: Date.now(),
        });
      } else {
        assignRandom();
        persistGps({ status: "error", message: "No se pudo resolver tu zona." });
      }
      done(gpsCoordsRef.current);
    };
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        resolveWithCoords(coords).catch(() => {
          if (gpsRequestRef.current !== requestId) return;
          assignRandom();
          persistGps({ status: "error", message: "No se pudo resolver tu zona." });
          done(null);
        });
      },
      (error) => {
        if (gpsRequestRef.current !== requestId) return;
        assignRandom();
        const message =
          error?.code === 1
            ? "Permiso de ubicación denegado. Actívalo para maldecir ubicaciones."
            : error?.code === 2
              ? "Ubicación no disponible en este momento."
              : "Se agotó el tiempo del GPS. Intenta de nuevo.";
        persistGps({ status: "error", message });
        done(null);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  }), [setZone, setGps, gpsRequestRef, gpsCoordsRef]);
  return locate;
}
